import React, { useEffect } from "react";
import { Modal, Form, Input, Select, message } from "antd";
import axios from "axios";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import Constants from "../Utils/Constants";
import useToken from "../Authentication/useToken";

const { Option } = Select;

export default function CreateClinicModal({
  open,
  onClose,
  onCreated = null,
  initialPostalCode = "",
}) {
  const [form] = Form.useForm();
  const { t } = useTranslation(["App", "Backend"]);
  const { token } = useToken();

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (initialPostalCode) form.setFieldsValue({ postalCode: initialPostalCode });
    }
  }, [open, initialPostalCode, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      const res = await axios.post(`${Constants.SERVER_URL}/clinics`, values, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success(t("clinic_success_created", "Clinique créée avec succès"));
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      if (err?.response?.data?.message) {
        message.error(err.response.data.message);
      }
    }
  };

  return (
    <Modal
      title={t("clinic_create_modal_title", "Ajouter un établissement")}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText={t("clinic_create_modal_ok", "Créer")}
      cancelText={t("clinic_create_modal_cancel", "Annuler")}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t("clinic_form_establishment_name", "Nom de l’établissement")}
          name="name"
          rules={[{ required: true, message: t("clinic_form_name_required", "Le nom est obligatoire") }]}
        >
          <Input placeholder={t("clinic_form_name_placeholder", "Ex: CHSLD Résidence Demo")} />
        </Form.Item>

        <Form.Item
          label={t("clinic_form_type", "Type")}
          name="type"
          rules={[{ required: true, message: t("clinic_form_type_required", "Le type est obligatoire") }]}
        >
          <Select placeholder={t("clinic_form_type_placeholder", "Choisir un type")}>
            <Option value="CHSLD">CHSLD</Option>
            <Option value="RPA">RPA</Option>
            <Option value="GMF">{t("clinic_type_gmf", "Clinique de médecine familiale (GMF)")}</Option>
            <Option value="HOPITAL">{t("clinic_type_hospital", "Hôpital")}</Option>
            <Option value="CLINIQUE">{t("clinic_type_clinic", "Clinique")}</Option>
            <Option value="AUTRE">{t("clinic_type_other", "Autre")}</Option>
          </Select>
        </Form.Item>

        <Form.Item label={t("clinic_form_postal_code", "Code postal")} name="postalCode">
          <Input placeholder={t("clinic_form_postal_code_placeholder", "Ex: J4Y 1R3")} />
        </Form.Item>

        <Form.Item label={t("clinic_form_address", "Adresse")} name="addressLine">
          <Input placeholder={t("clinic_form_address_placeholder", "Ex: 123 Rue Test")} />
        </Form.Item>

        <Form.Item label={t("clinic_form_city", "Ville")} name="city">
          <Input placeholder={t("clinic_form_city_placeholder", "Ex: Brossard")} />
        </Form.Item>

        <Form.Item label={t("clinic_form_province", "Province")} name="province">
          <Input placeholder={t("clinic_form_province_placeholder", "Ex: QC")} />
        </Form.Item>

        <Form.Item label={t("clinic_form_phone", "Téléphone")} name="phone">
          <Input placeholder={t("clinic_form_phone_placeholder", "Ex: 514-000-0000")} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

CreateClinicModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  initialPostalCode: PropTypes.string,
};
